import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/auth.store';
import { useTheme } from './store/settings.store';
import { useBreakpoint } from './hooks/useBreakpoint';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ResetPassword from './pages/ResetPassword';
import DuoCallback from './pages/DuoCallback';
import Dashboard from './pages/Dashboard';
import MapPage from './pages/MapPage';
import DevicesPage from './pages/DevicesPage';
import AlertsPage from './pages/AlertsPage';
import AlertRulesPage from './pages/AlertRulesPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import ReportsPage from './pages/ReportsPage';
import ScheduledReportsPage from './pages/ScheduledReportsPage';
import MaintenancePage from './pages/MaintenancePage';
import FuelPage from './pages/FuelPage';
import GroupsPage from './pages/GroupsPage';
import DriversPage from './pages/DriversPage';
import MissionsPage from './pages/MissionsPage';
import MyMissionsPage from './pages/MyMissionsPage';
import ParcelsPage from './pages/ParcelsPage';
import MyParcelsPage from './pages/MyParcelsPage';
import PublicParcelTrackPage from './pages/PublicParcelTrackPage';
import EvDashboardPage from './pages/EvDashboardPage';
import EvChargingMapPage from './pages/EvChargingMapPage';
import TourOptimizationPage from './pages/TourOptimizationPage';
import MyToursPage from './pages/MyToursPage';
import DriverScoringPage from './pages/DriverScoringPage';
import GeofencesPage from './pages/GeofencesPage';
import UsersPage from './pages/UsersPage';
import BillingPage from './pages/BillingPage';
import OnboardingPage from './pages/OnboardingPage';
import AcceptCguPage from './pages/AcceptCguPage';
import Setup2FAPage from './pages/Setup2FAPage';
import SharePage from './pages/SharePage';
import ApiKeysPage from './pages/ApiKeysPage';
import BrandingPage from './pages/BrandingPage';
import HelpPage from './pages/HelpPage';
import BeaconsPage from './pages/BeaconsPage';
import PrivacyPage from './pages/PrivacyPage';
import TrackPage from './pages/TrackPage';
import {
  AdminDashboard,
  TenantsPage,
  SubscriptionsPage,
  AuditLogsPage,
  NotificationsSettingsPage,
  AppearancePage,
  MarketingPage,
  EvModelsPage,
  TrackerParametersPage,
  DevicesAdminPage,
  StripeSettingsPage,
  OemSettingsPage,
  SystemPage,
  AdminSettingsPage,
  BeaconsAdminPage,
  InvoicesPage,
  IpSecurityPage,
} from './pages/admin';

// Responsive home redirect (map for mobile/tablet, dashboard for desktop)
function HomeRedirect() {
  const { isDesktop } = useBreakpoint();
  const user = useAuthStore((state) => state.user);

  // Drivers always land on their missions page, desktop or mobile
  if (user?.role === 'DRIVER') {
    return <Navigate to="/my-missions" replace />;
  }

  // SUPER_ADMIN and ADMIN always have full access — go to the natural landing
  if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') {
    return <Navigate to={isDesktop ? '/dashboard' : '/map'} replace />;
  }

  // Other roles (MANAGER/OPERATOR/VIEWER/custom): pick the first page they
  // actually have permission to see, in a sensible order for the device type.
  const hasPermission = (key: string): boolean =>
    !!user?.tenantPermissions?.some((p) => p.startsWith(key + ':'));

  const desktopOrder = ['dashboard', 'map', 'devices', 'alerts', 'history'];
  const mobileOrder = ['map', 'dashboard', 'alerts', 'devices', 'history'];
  const order = isDesktop ? desktopOrder : mobileOrder;
  for (const key of order) {
    if (hasPermission(key)) {
      return <Navigate to={`/${key}`} replace />;
    }
  }
  // Fallback — every authenticated user can see Help
  return <Navigate to="/help" replace />;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // SUPER_ADMIN exempt from CGU and 2FA checks
  if (user?.role !== 'SUPER_ADMIN') {
    if (!user?.cguAccepted) return <Navigate to="/accept-cgu" replace />;
    if (user?.mustSetup2FA) return <Navigate to="/setup-2fa" replace />;
  }

  // DRIVER role: forcibly redirect to /my-missions if they land on a page
  // they don't have permission for. Defensive against PWA shortcuts, browser
  // bookmarks, or any URL they might keep from a previous session.
  if (user?.role === 'DRIVER') {
    const allowedPaths = ['/my-missions', '/my-parcels', '/my-tours', '/help', '/accept-cgu', '/setup-2fa', '/settings'];
    const isAllowed = allowedPaths.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + '/'),
    );
    if (!isAllowed) {
      return <Navigate to="/my-missions" replace />;
    }
  }

  return <Layout>{children}</Layout>;
}

// Admin route wrapper (requires SUPER_ADMIN role, exempt from CGU/2FA)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'SUPER_ADMIN') {
    return <HomeRedirect />;
  }

  return <Layout>{children}</Layout>;
}

// Onboarding route wrapper (no Layout)
function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  if (isAuthenticated) {
    return <HomeRedirect />;
  }

  return <>{children}</>;
}

export default function App() {
  // Apply theme to document
  useTheme();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route path="/duo-callback" element={<DuoCallback />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/track/:token" element={<TrackPage />} />
      <Route path="/track-parcel/:token" element={<PublicParcelTrackPage />} />

      {/* Protected routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/map"
        element={
          <ProtectedRoute>
            <MapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/devices"
        element={
          <ProtectedRoute>
            <DevicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts/rules"
        element={
          <ProtectedRoute>
            <AlertRulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/share"
        element={
          <ProtectedRoute>
            <SharePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/scheduled"
        element={
          <ProtectedRoute>
            <ScheduledReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/maintenance"
        element={
          <ProtectedRoute>
            <MaintenancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fuel"
        element={
          <ProtectedRoute>
            <FuelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups"
        element={
          <ProtectedRoute>
            <GroupsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drivers"
        element={
          <ProtectedRoute>
            <DriversPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/drivers/scores"
        element={
          <ProtectedRoute>
            <DriverScoringPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/missions"
        element={
          <ProtectedRoute>
            <MissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-missions"
        element={
          <ProtectedRoute>
            <MyMissionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/parcels"
        element={
          <ProtectedRoute>
            <ParcelsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-parcels"
        element={
          <ProtectedRoute>
            <MyParcelsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ev"
        element={
          <ProtectedRoute>
            <EvDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ev/charging-map"
        element={
          <ProtectedRoute>
            <EvChargingMapPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tours"
        element={
          <ProtectedRoute>
            <TourOptimizationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tours"
        element={
          <ProtectedRoute>
            <MyToursPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/geofences"
        element={
          <ProtectedRoute>
            <GeofencesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute>
            <ApiKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/branding"
        element={
          <ProtectedRoute>
            <BrandingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/beacons"
        element={
          <ProtectedRoute>
            <BeaconsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accept-cgu"
        element={
          <OnboardingRoute>
            <AcceptCguPage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/setup-2fa"
        element={
          <OnboardingRoute>
            <Setup2FAPage />
          </OnboardingRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <OnboardingRoute>
            <OnboardingPage />
          </OnboardingRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tenants"
        element={
          <AdminRoute>
            <TenantsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/subscriptions"
        element={
          <AdminRoute>
            <SubscriptionsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/invoices"
        element={
          <AdminRoute>
            <InvoicesPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <AdminRoute>
            <NotificationsSettingsPage />
          </AdminRoute>
        }
      />
      {/* Redirect old email path to notifications */}
      <Route
        path="/admin/email"
        element={<Navigate to="/admin/notifications" replace />}
      />
      <Route
        path="/admin/audit-logs"
        element={
          <AdminRoute>
            <AuditLogsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/appearance"
        element={
          <AdminRoute>
            <AppearancePage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/marketing"
        element={
          <AdminRoute>
            <MarketingPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/tracker-parameters"
        element={
          <AdminRoute>
            <TrackerParametersPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/ev-models"
        element={
          <AdminRoute>
            <EvModelsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/devices"
        element={
          <AdminRoute>
            <DevicesAdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/stripe"
        element={
          <AdminRoute>
            <StripeSettingsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/oem"
        element={
          <AdminRoute>
            <OemSettingsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/system"
        element={
          <AdminRoute>
            <SystemPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <AdminSettingsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/beacons"
        element={
          <AdminRoute>
            <BeaconsAdminPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/ip-security"
        element={
          <AdminRoute>
            <IpSecurityPage />
          </AdminRoute>
        }
      />

      {/* Redirect root to map (mobile/tablet) or dashboard (desktop) */}
      <Route path="/" element={<HomeRedirect />} />

      {/* 404 */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center bg-dark-950">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">404</h1>
              <p className="text-dark-400 mt-4 text-lg">Page non trouvée</p>
              <a href="/dashboard" className="mt-6 inline-block btn-glow">
                Retour au tableau de bord
              </a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}
